// react
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';

// material-ui
import Grid from '@material-ui/core/Grid/Grid';
import Typography from '@material-ui/core/Typography/Typography';
import Paper from '@material-ui/core/Paper/Paper';
import withTheme, { WithTheme } from '@material-ui/core/styles/withTheme';

// project files
import * as styles from './YourAnalyst.css';
import { CONTACT_SUBMIT } from '../../ducks/actions';
import { AscendState } from '../../ducks';
import Title from '../shared/Title/Title';
import Layout from '../Layout/Layout';
import StarRating from '../StarRating/StarRating';
import ContactForm from '../Contact/ContactForm/ContactForm';
import CalendlyFrame from '../CalendlyFrame/CalendlyFrame';

type AscendReview = {
  comment: string;
  displayName: string;
  profilePhotoUrl: string;
  starRating: number;
};

type StateProps = {
  advisorChatPhotoUrl: string;
  advisorReviews?: AscendReview[];
  advisorName: string;
  contactMessageSubmitted: boolean;
  name: string;
  email: string;
};

type DispatchProps = {
  submitContact: (message: string) => void;
};

type AnalystProps = StateProps & DispatchProps;

const YourAnalyst = ({
  advisorChatPhotoUrl,
  advisorReviews,
  advisorName,
  name,
  email,
  contactMessageSubmitted,
  submitContact,
  theme,
}: AnalystProps & WithTheme) => (
  <Layout>
    <Title text={`Meet Your Analyst, ${advisorName}`} center />
    <Grid container justify="center" alignItems="center" direction="column">
      <Grid
        container
        justify="center"
        alignItems="center"
        direction="column"
        style={{ marginBottom: '4rem' }}>
        {advisorChatPhotoUrl && <img src={advisorChatPhotoUrl} alt="Analyst Photo" />}

        <Typography
          variant="title"
          align="center"
          color="inherit"
          paragraph
          style={{ marginTop: '2rem', marginBottom: '-1rem' }}>
          I would love to chat with you!
        </Typography>
        <CalendlyFrame />
      </Grid>
      {!contactMessageSubmitted ? (
        <ContactForm
          initialValues={{ name, email }}
          onSubmit={(data: { message: string }) => submitContact(data.message)}
        />
      ) : (
        <Grid container alignItems="center" justify="center">
          <Grid item xs={11} container alignItems="center" justify="center">
            <Paper
              style={{
                width: '100%',
                padding: '15px',
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
              }}>
              <Typography variant="display1" align="center" color="inherit" gutterBottom>
                Success!
              </Typography>
              <Typography variant="subheading" align="center" color="inherit" paragraph>
                You have successfully submitted your contact message, and we will respond to you
                through email promptly!
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
      <Grid container justify="center" alignItems="center" direction="column">
        <Typography variant="title" align="center" color="inherit" paragraph>
          {advisorName}'s Reviews
        </Typography>
        {!!advisorReviews &&
          !!advisorReviews.length &&
          advisorReviews.map((review: AscendReview, index: number) => (
            <Grid item key={index} className={styles.reviewGrid}>
              {review.profilePhotoUrl ? (
                <div className="reviewer">
                  <span className="reviewerImageContainer">
                    <img src={review.profilePhotoUrl} className="reviewPhoto" alt="Analyst Photo" />
                  </span>
                  <span>
                    <Typography variant="body1">{review.displayName}</Typography>
                    <StarRating starCount={review.starRating} />
                  </span>
                </div>
              ) : (
                <StarRating starCount={review.starRating} />
              )}
              <Typography variant="body2" align="left" color="inherit" paragraph>
                {review.comment}
              </Typography>
            </Grid>
          ))}
      </Grid>
    </Grid>
  </Layout>
);

const mapStateToProps = (state: AscendState) => ({
  advisorName: state.user.data.advisorName,
  advisorChatPhotoUrl: state.user.data.advisorChatPhotoUrl,
  advisorReviews: state.user.data.advisorReviews,
  name: `${state.user.data.firstName} ${state.user.data.lastName}`,
  email: state.user.data.email,
  contactMessageSubmitted: state.user.contactMessageSubmitted,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  submitContact: (message: string) => dispatch({ type: CONTACT_SUBMIT, message }),
});
export default withTheme()(
  connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(YourAnalyst)
);
